import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import RegisterForm from '../../../components/forms/RegisterForm'
import { type RegisterFormValues } from '../../../components/forms/RegisterForm/RegisterForm.type'
import { Heading } from '../../../components/typography'
import { useCreateMoveAction, useSaveMoveAction } from '../../../modules/moves'
import { type QuotesRequestFormValues } from '../QuotesRequestPage/QuotesRequestPage.type'
import { formatMoveRequest } from '../QuotesRequestPage/utils/formatRequest'
import { useNavigate, usePathGenerator, useQueryString } from '../../../modules/navigation'
import Route from '../../../app/Route.enum'
import { useErrorFormatter } from '../../../components/errors/useErrorFormatter'
import { errorNotification } from '../../../components/ToastNotifications'
import { useDefaultValues } from '../../../components/forms/RegisterForm/useDefaultValues'
import Spinner from '../../../components/Spinner'

type QuotesPreviewAgentProps = {
  requestFormValues: QuotesRequestFormValues
}

const QuotesPreviewAgent: React.FC<QuotesPreviewAgentProps> = ({
  requestFormValues,
}) => {
  const { t } = useTranslation()
  const [createMove] = useCreateMoveAction()
  const saveMove = useSaveMoveAction()
  const { navigate } = useNavigate()
  const generatePath = usePathGenerator()
  const formatError = useErrorFormatter()
  const { coupon: promoCode, ...queryString } = useQueryString() ?? {}
  const { data: providedData, valid: skipRegisterForm } = useDefaultValues()
  const called = useRef(false)

  const handleCreateMove = async (userFormValues: RegisterFormValues) => {
    const payload = formatMoveRequest({
      requestFormValues,
      userFormValues,
      queryString,
    })
    try {
      const move = await createMove(payload, promoCode)
      if (move) {
        saveMove(move)
        navigate(generatePath(Route.Move, { moveId: move.id }))
      }
    } catch (error) {
      errorNotification(formatError(error))
    }
  }

  useEffect(() => {
    if (!skipRegisterForm || called.current) {
      return
    }
    called.current = true
    handleCreateMove(providedData as RegisterFormValues).catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipRegisterForm, handleCreateMove])

  if (skipRegisterForm) {
    return (
      <div className="my-16 flex items-center justify-center">
        <Spinner expanded />
      </div>
    )
  }

  return (
    <div className="pt-4 text-center">

      <Heading variant="h1">
        { t('pages.quotesRequest.preliminaryQuotes.agent.title') }
      </Heading>
      <div className="my-8 font-sans text-lg">
        { t('pages.quotesRequest.preliminaryQuotes.agent.explanation') }
      </div>
      <div className="my-16">
        <Heading variant="h2" className="text-center">
          { t('pages.quotesRequest.preliminaryQuotes.agent.register.title') }
        </Heading>

        <RegisterForm
          onSubmit={handleCreateMove}
          whiteQuotes={false}
        />
      </div>
    </div>
  )
}

export default QuotesPreviewAgent
